import React, { useMemo, useRef } from 'react'
import WSManager from '../../connection/websocket'
import { RacingError } from '../../error'
import RacingManager from '../../managers/racing'
import { filter, forEach, forOwn, get, isArray, isEmpty } from 'lodash'
import { getUID, logger } from '../../utils'
import { DateService } from '../../services'
import { useApplicationState } from '../root'
import { useSettings } from '../settings'
import { RaceMarketsTabs, RaceTypesMap } from '../../constants'
import Bus from '../../bus'
import { RACING_ANY_RACE_STATUS_UPDATE } from '../../connection/subscription/subscription.topics'

export const RacingActions = {
  ADD_LOADER: 'ADD_LOADER',
  REMOVE_LOADER: 'REMOVE_LOADER',
  ADD_SUBSCRIPTION: 'ADD_SUBSCRIPTION',
  REMOVE_SUBSCRIPTION: 'REMOVE_SUBSCRIPTION',
  ClEAR_ALL_SUBSCRIPTIONS: 'ClEAR_ALL_SUBSCRIPTIONS',
  RACING_STATE_UPDATE: 'RACING_STATE_UPDATE',
  RACING_RESET_STATE: 'RACING_RESET_STATE',
  RACING_OPTIMISE_STATE: 'RACING_OPTIMISE_STATE',
  RACING_MEETS_UPDATE: 'RACING_MEETS_UPDATE',
  RACING_PATH_UPDATE: 'RACING_PATH_UPDATE',
  RACING_SET_ACTIVE_RACE_ID: 'RACING_SET_ACTIVE_RACE_ID',
  RACING_SET_ACTIVE_RACE: 'RACING_SET_ACTIVE_RACE',
  RACING_RACE_UPDATE: 'RACING_RACE_UPDATE',
  RACING_NEXT_RACES: 'RACING_NEXT_RACES',
  RACING_RACE_ODDS_HISTORIES_UPDATE: 'RACING_RACE_ODDS_HISTORIES_UPDATE',
  RACING_ADD_BETSLIP_RUNNERS: 'RACING_ADD_BETSLIP_RUNNERS',
  RACING_ADD_MULTI_BETSLIP_RUNNERS: 'RACING_ADD_MULTI_BETSLIP_RUNNERS',
  RACING_CLEAR_BETSLIP_RUNNERS: 'RACING_CLEAR_BETSLIP_RUNNERS',
  RESET_ERROR: 'RESET_ERROR'
}

const racingInitialState = {
  initialized: false,
  loading: false,
  activeLoaders: 0,
  error: null,
  lastRacesUpdate: null,

  subscribeRaceIds: [],
  dates: [],
  marketTypes: [],
  raceTypes: [],
  countries: [],
  venues: [],
  meetings: [],
  races: [],
  raceDate: null,
  raceType: null,
  raceCountry: null,
  raceVenue: null,
  raceNumber: null,
  activeRaceId: null,
  activeRace: null,
  activeMeet: null,
  activeMarketTypeId: RaceMarketsTabs.WP_TAB,
  marketsByTypeConfig: {},

  // These are only used for the activeRace
  marketsByType: {},
  runnersById: {},
  outcomesByRunnerId: {},
  srmSelections: [],
  combinedSelections: [],
  quinSelections: [],
  exactaSelections: [],
  trifectaSelections: [],
  first4Selections: [],

  // These are used in the betslip
  betslipRunnersByEventId: {}

  // These fields will be generated during state initialisation
  // id: null,
  // connected: null,
  // connectionId: null,
}

const TODAY = 'today'

const NEXT_RACES_LOADING_TIMEOUT_MS = 10000

const THIRTY_MINUTES = 30 * 60 * 1000

const raceHasAcitveMarkets = (race) => {
  /* if (race?.status !== RACE_STATUS.OPEN) {
    return true;
  } */

  const projectId = get(window.SYS_CONFIG, 'projectId', '')

  if (!race?.hasActiveMarkets) {
    return false
  }

  const hasMarkets = race.hasActiveMarkets[projectId] === undefined ? race.hasActiveMarkets[0] : race.hasActiveMarkets[projectId]

  // console.log('raceHasAcitveMarkets', race, hasMarkets)

  return hasMarkets
}

const createRacingInitialState = options => {
  const {
    ...restOptions
  } = options

  const id = getUID(5)
  const connected = WSManager.connected
  const connectionId = WSManager.connectionId

  return {
    id,
    connected,
    connectionId,
    ...racingInitialState,
    ...restOptions
  }
}

const parseSingleRace = (state, race) => {
  const markets = race.markets || []
  const marketsByType = markets.reduce((list, m) => {
    const key = `${m.type}${m.legs ? `:${m.legs}` : ''}`
    return {
      ...list,
      [key]: {
        ...state.marketsByTypeConfig[key] || {},
        ...m,
        raceId: race.raceId
      }
    }
  }, { ...state.marketsByType })

  const runners = race.runners || []
  const runnersById = runners.reduce((list, r) => {
    return {
      ...list,
      [r.runnerId]: {
        ...r,
        raceId: race.raceId
      }
    }
  }, { ...state.runnersById })

  const outcomesByRunnerId = (Object.values(runnersById) || []).reduce((list, r) => {
    return {
      ...list,
      [r.runnerId]: (Object.values(marketsByType) || []).reduce((list2, m) => {
        return {
          ...list2,
          [m.type]: (m.outcomes || []).find(o => o.specifier === r.number.toString())
        }
      }, {})
    }
  }, { ...state.outcomesByRunnerId })

  return {
    marketsByType,
    runnersById,
    outcomesByRunnerId
  }
}

const addRaceOddsHistoriesToState = (state, oddsHistories) => {
  const existingRace = state.races.find((r) => r.raceId === oddsHistories.raceId)
  if (!existingRace) {
    return state
  }
  return addRaceToState(state, {
    ...existingRace,
    fwinOutcomesWithOddsHistories: oddsHistories.fwinOutcomesWithOddsHistories
  })

  // const races = state.races.reduce((list, r) => {
  //   // add fwinOutcomesWithOddsHistories to race
  //   return [
  //     ...list,
  //     (r.raceId === oddsHistories.raceId)
  //       ? ({ ...r, fwinOutcomesWithOddsHistories: oddsHistories.fwinOutcomesWithOddsHistories })
  //       : r
  //   ]
  // }, [])
  //
  // const activeRace = state.activeRace?.raceId === oddsHistories.raceId
  //   ? races.find(r => r.raceId === oddsHistories.raceId)
  //   : state.activeRace
  //
  // return {
  //   ...state,
  //   activeRace,
  //   races
  // }
}

const addRaceToState = (state, newRace) => {
  newRace = {
    ...newRace,
    raceType: {
      id: newRace.type,
      typeName: RaceTypesMap[newRace.type]?.typeName
    },
    meeting: {
      meetingId: newRace.meetingId,
      date: newRace.date
    },
    venue: {
      venueId: newRace.venueId,
      country: newRace.country,
      venueName: newRace.venueName || newRace.venue?.venueName
    }
  }

  const oldRace = state.races.find(r => r.raceId === newRace.raceId)
  if (oldRace != null) {
    let runners = oldRace.runners
    if (isEmpty(newRace.runners) === false) {
      runners = newRace.runners
    }

    let markets = oldRace.markets
    if (isEmpty(newRace.markets) === false) {
      markets = newRace.markets
    }

    let closedMarketTypes = oldRace.closedMarketTypes
    if (isEmpty(newRace.closedMarketTypes) === false) {
      closedMarketTypes = newRace.closedMarketTypes
    }

    newRace = {
      ...oldRace,
      ...newRace,
      runners,
      markets,
      closedMarketTypes
    }
  }

  const activeRace = (state.activeRaceId === newRace.raceId)
    ? newRace
    : state.activeRace

  const activeMeet = (state.meetings || []).find((m) => (m.meetingId === activeRace?.meetingId))

  // const races = state.races.reduce((list, r) => {
  //   return [
  //     ...list,
  //     (r.raceId === newRace.raceId) ? newRace : r
  //   ]
  // }, [])

  const races = [
    ...(state.races || []).filter(r => r.raceId !== newRace.raceId),
    newRace
  ].filter(r => raceHasAcitveMarkets(r))

  const venues = state.venues.reduce((list, v) => {
    let venueRaces = v.races
    const idx = venueRaces.findIndex(r => r.raceId === newRace.raceId)
    if (idx >= 0) {
      venueRaces = [
        ...venueRaces.slice(0, idx),
        newRace,
        ...venueRaces.slice(idx + 1)
      ]
    }
    return [
      ...list,
      {
        ...v,
        races: venueRaces
      }
    ]
  }, [])

  const meetings = state.meetings.reduce((list, m) => {
    let meetRaces = m.races
    const idx = meetRaces.findIndex(r => r.raceId === newRace.raceId)
    if (idx >= 0) {
      meetRaces = [
        ...meetRaces.slice(0, idx),
        newRace,
        ...meetRaces.slice(idx + 1)
      ]
    } else if (m.meetingId === newRace.meetingId) {
      let newPos = 0
      while (newPos < meetRaces.length && meetRaces[newPos].number < newRace.number) {
        newPos++
      }
      meetRaces = [
        ...meetRaces.slice(0, newPos),
        newRace,
        ...meetRaces.slice(newPos)
      ]
    }
    return [
      ...list,
      {
        ...m,
        races: meetRaces
      }
    ]
  }, [])

  const {
    marketsByType,
    runnersById,
    outcomesByRunnerId
  } = parseSingleRace(state, newRace)

  return {
    ...state,
    activeRace,
    activeMeet,
    races,
    venues: venues.filter(v => v.races?.length > 0),
    meetings: meetings.filter(m => m.races?.length > 0),
    ...(activeRace?.raceId === newRace?.raceId ? { // update it only for active race
      marketsByType,
      runnersById,
      outcomesByRunnerId
    } : {}
    )
  }
}

const addMeetingsToState = (state, meetings) => {
  const raceTypes = {}
  const countries = {}
  const venues = {}
  const meets = {}
  const races = {}

  // Maintain existing meets, but ignore old meets if duplicated in the new payload
  const newMeets = meetings || []
  const oldMeets = (state.meetings || []).filter(om => {
    if (newMeets.some(nm => nm.meetingId === om.meetingId)) {
      return false
    }
    return true
  })

  forEach([...newMeets, ...oldMeets], (meet, index) => {
    if (raceTypes[meet.type] == null) {
      raceTypes[meet.type] = {
        id: meet.type,
        typeName: RaceTypesMap[meet.type]?.typeName
      }
    }

    if (countries[meet.country] == null) {
      countries[meet.country] = {
        countryId: meet.country,
        countryName: meet.countryName,
        venues: []
      }
    }

    if (venues[meet.venueId] == null) {
      venues[meet.venueId] = {
        venueId: meet.venueId,
        venueName: meet.venueName,
        venueOrder: meet.venueOrder,
        country: meet.country,
        countryName: meet.countryName,
        state: meet.state,
        type: meet.type,
        tabCode: meet.tabCode,
        raceType: raceTypes[meet.type],
        races: []
      }
    }

    meets[meet.meetingId] = {
      meetingId: meet.meetingId,
      raceType: raceTypes[meet.type],
      country: countries[meet.country],
      venue: venues[meet.venueId],
      ...meet
    }

    forEach(meet.races, (race, index) => {
      if (!raceHasAcitveMarkets(race)) {
        // console.log('raceHasAcitveMarkets skip')
        return
      }

      races[race.raceId] = {
        raceId: race.raceId,
        raceType: raceTypes[meet.type],
        countryId: meet.country,
        country: countries[meet.country],
        venueId: meet.venueId,
        venue: venues[meet.venueId],
        meetingId: meet.meetingId,
        meeting: meets[meet.meetingId],
        runners: [],
        markets: [],
        ...race
      }

      // The above often removes the Active Race's runners and Markets
      const isActiveRace = (state.activeRace?.raceId === race.raceId)
      if (isActiveRace && isEmpty(races[race.raceId].runners)) {
        races[race.raceId].runners = state.activeRace.runners
        races[race.raceId].markets = state.activeRace.markets
      }
    })
  })

  // Add races to meetings
  forOwn(meets, (value, key) => {
    meets[key].races = filter(races, (r) => r.meetingId.toString() === key)
  })

  // Add races to venues
  forOwn(venues, (value, key) => {
    venues[key].races = filter(races, (r) => r.venueId.toString() === key)
  })

  // Add venues to countries
  forOwn(countries, (value, key) => {
    countries[key].venues = filter(venues, ['country', key])
  })

  // Update Active Meet
  const finalMeetings = Object.values(meets).filter(m => m.races?.length > 0)
  const activeMeet = (finalMeetings || []).find((m) => (m.meetingId === state.activeRace?.meetingId))

  return {
    ...state,
    raceTypes: Object.values(raceTypes),
    countries: Object.values(countries),
    venues: Object.values(venues).filter(v => v.races?.length > 0),
    meetings: finalMeetings,
    races: Object.values(races),
    activeMeet
  }
}

const racingReducer = (state, action) => {
  logger.debug(`[racing:${state.id}] dispatching action`, action)

  switch (action.type) {
    case RacingActions.RESET_ERROR: {
      return {
        ...state,
        ...(action.payload || {}),
        error: null
      }
    }

    case RacingActions.ADD_LOADER: {
      const activeLoaders = state.activeLoaders += 1
      return {
        ...state,
        ...(action.payload || {}),
        activeLoaders,
        loading: true
      }
    }

    case RacingActions.REMOVE_LOADER: {
      let activeLoaders = state.activeLoaders -= 1
      if (activeLoaders < 0) {
        activeLoaders = 0
      }
      const loading = activeLoaders !== 0
      return {
        ...state,
        ...(action.payload || {}),
        activeLoaders,
        loading
      }
    }

    case RacingActions.ADD_SUBSCRIPTION: {
      const raceId = action.payload?.raceId
      const oldSubscribeRaceIds = state.subscribeRaceIds

      if (!raceId) {
        return state
      }

      if (!oldSubscribeRaceIds.includes(raceId)) {
        oldSubscribeRaceIds.push(raceId)
      }

      return {
        ...state,
        subscribeRaceIds: [...oldSubscribeRaceIds]
      }
    }

    case RacingActions.REMOVE_SUBSCRIPTION: {
      const raceId = action.payload?.raceId

      if (!raceId) {
        return state
      }

      return {
        ...state,
        subscribeRaceIds: [...state.subscribeRaceIds?.filter((rId) => rId !== raceId)]
      }
    }

    case RacingActions.ClEAR_ALL_SUBSCRIPTIONS: {
      return {
        ...state,
        subscribeRaceIds: []
      }
    }

    case RacingActions.RACING_RESET_STATE: {
      return {
        ...state,
        initialized: false,
        loading: false,
        activeLoaders: 0,
        error: null,
        lastRacesUpdate: null,
        dates: [],
        marketTypes: [],
        raceTypes: [],
        countries: [],
        venues: [],
        meetings: [],
        races: []
      }
    }

    case RacingActions.RACING_OPTIMISE_STATE: {
      const dates = (action.payload.dates || state.dates)?.map(d => d.date)
      const activeDates = state.raceDate
        ? [
          state.raceDate,
          ...dates
        ]
        : dates

      const meetingsToPrune = []
      const racesToPrune = [];

      (state.meetings || []).forEach(m => {
        if (!activeDates.some(d => d === m.date)) {
          (m.races || []).forEach(rc => {
            racesToPrune.push(rc)
          })
          meetingsToPrune.push(m)
        }
      })

      const meetings = state.meetings.filter(m => !meetingsToPrune.some(m2 => m2.meetingId === m.meetingId))
      const races = state.races.filter(r => !racesToPrune.some(r2 => r2.raceId === r.raceId))

      return {
        ...state,
        ...action.payload,
        meetings,
        races
      }
    }

    case RacingActions.RACING_STATE_UPDATE:
      return {
        ...state,
        ...action.payload
      }

    case RacingActions.RACING_MEETS_UPDATE: {
      return addMeetingsToState({
        ...state,
        lastRacesUpdate: new Date()
      }, action.payload)
    }

    case RacingActions.RACING_PATH_UPDATE:
      return {
        ...state,
        ...action.payload
      }

    case RacingActions.RACING_SET_ACTIVE_RACE_ID: {
      return {
        ...state,
        activeRaceId: action.payload
      }
    }

    case RacingActions.RACING_SET_ACTIVE_RACE: {
      return addRaceToState({
        ...state,
        marketsByType: {},
        runnersById: {},
        outcomesByRunnerId: {}
      }, action.payload)
    }

    case RacingActions.RACING_RACE_UPDATE: {
      const oldRace = state.races.find(r => r.raceId === action.payload?.raceId)

      const outcomesWithOddsHistories = { ...oldRace?.fwinOutcomesWithOddsHistories || {} }
      const fWinMarket = action.payload?.markets?.find(m => m.type === 'F_WIN' && !oldRace?.closedMarketTypes?.includes(m.type))

      if (oldRace?.fwinOutcomesWithOddsHistories && fWinMarket && action.payload.updatedFixedOdds) {
        // update fwinOutcomesWithOddsHistories in state

        Object.keys(outcomesWithOddsHistories).forEach((key) => {
          const outcome = (fWinMarket.outcomes || []).find(o => o.outcomeId === outcomesWithOddsHistories[key]?.outcomeId)

          if (outcome?.odds && outcome?.odds !== outcomesWithOddsHistories[key]?.odds) {
            // if odds changed
            if (outcomesWithOddsHistories[key].oddsHistories?.length) {
              const newOddsHistories = [...outcomesWithOddsHistories[key].oddsHistories]
              // add new odds for the outcome in the end of history array
              newOddsHistories.push({
                index: outcomesWithOddsHistories[key].oddsHistories[outcomesWithOddsHistories[key].oddsHistories.length - 1].index + 1,
                odds: outcome.odds,
                time: fWinMarket.updateTime
              })

              if (newOddsHistories.length > 10) {
                // not more than 10 elements in history, keep the “Open” odds with index 0, remove the oldest
                newOddsHistories.splice(1, 1)
              }

              outcomesWithOddsHistories[key].oddsHistories = newOddsHistories
            }
            outcomesWithOddsHistories[key].odds = outcome.odds
          }
        })
      }

      const markets = oldRace?.closedMarketTypes
        ? action.payload?.markets?.filter(m => !oldRace.closedMarketTypes.includes(m.type))
        : action.payload.markets

      return addRaceToState(state, {
        ...action.payload,
        markets,
        fwinOutcomesWithOddsHistories: outcomesWithOddsHistories
      })
    }

    case RacingActions.RACING_NEXT_RACES: {
      return (action.payload.races || [])
        .reduce((newState, race) => {
          return addRaceToState(newState, race)
        }, {
          ...state,
          lastRacesUpdate: new Date()
        })

      // Other races are added to state but do not include these unique raceType, meeting, venue models
      // const races = [...state.races];
      //
      // (action.payload.races || []).forEach(nr => {
      //   if (!races.find(r => r.raceId === nr.raceId)) {
      //     races.push({
      //       ...nr,
      //       raceType: {
      //         id: nr.type,
      //         typeName: RaceTypesMap[nr.type]?.typeName
      //       },
      //       meeting: {
      //         meetingId: nr.meetingId,
      //         date: nr.date
      //       },
      //       venue: {
      //         venueId: nr.venueId,
      //         country: nr.country,
      //         venueName: nr.venueName
      //       }
      //     })
      //   }
      // })
      //
      // return {
      //   ...state,
      //   races
      // }
    }

    case RacingActions.RACING_RACE_ODDS_HISTORIES_UPDATE: {
      return addRaceOddsHistoriesToState(state, action.payload)
    }

    case RacingActions.RACING_ADD_BETSLIP_RUNNERS: {
      const bet = action.payload
      if (!bet || !bet.event || !bet.event.runners) {
        return state
      }
      return {
        ...state,
        betslipRunnersByEventId: {
          ...state.betslipRunnersByEventId,
          [bet.eventId]: bet.event.runners
        }
      }
    }

    case RacingActions.RACING_ADD_MULTI_BETSLIP_RUNNERS: {
      const races = action.payload
      if (!races || !races.length) {
        return state
      }

      const betslipRunnersByEventId = (races || []).reduce((list, race) => {
        const raceId = race.raceId
        return {
          ...list,
          [raceId]: race.runners || []
        }
      }, state.betslipRunnersByEventId)

      return {
        ...state,
        betslipRunnersByEventId
      }
    }

    case RacingActions.RACING_CLEAR_BETSLIP_RUNNERS: {
      return {
        ...state,
        betslipRunnersByEventId: {}
      }
    }

    default:
      return state
  }
}

export const createRacingState = options => {
  const [state, dispatcher] = React.useReducer(
    racingReducer,
    (options || {}),
    createRacingInitialState
  )

  const nextRacesLoadingRef = useRef()
  const { connection } = useApplicationState()
  const { lang } = useSettings()

  const loadOddsHistories = (raceId) => {
    const acOddsHistories = new AbortController()
    RacingManager.getRaceOddsHistories(raceId, acOddsHistories.signal)
      .then(data => {
        dispatcher({
          type: RacingActions.RACING_RACE_ODDS_HISTORIES_UPDATE,
          payload: {
            ...data,
            raceId
          }
        })
      })
      .catch(error => {
        console.log(error)
      })
    return acOddsHistories
  }

  const raceUpdateHandler = payload => {
    dispatcher({
      type: RacingActions.RACING_RACE_UPDATE,
      payload: payload.data.race
    })

    /* if (payload.data.race?.updatedFixedOdds) {
      loadOddsHistories(payload.data.race.raceId)
    } */
  }

  const raceReInitHandler = payload => {
    dispatcher({
      type: RacingActions.RACING_RESET_STATE
    })
  }

  const addBetslipRunnersHandler = payload => {
    dispatcher({
      type: RacingActions.RACING_ADD_BETSLIP_RUNNERS,
      payload: payload
    })
  }

  const clearBetslipRunnersHandler = () => {
    dispatcher({
      type: RacingActions.RACING_CLEAR_BETSLIP_RUNNERS,
      payload: null
    })
  }

  const loadEventRunnersHandler = (payload) => {
    // used to get runners info for By Bets and Bet Slip.
    // no need to add loader in this case.

    /* dispatcher({
      type: RacingActions.ADD_LOADER
    }) */

    RacingManager.getBasicRunnersInfo(payload)
      .then(data => {
        dispatcher({
          type: RacingActions.RACING_ADD_MULTI_BETSLIP_RUNNERS,
          payload: data.races
        })

        dispatcher({
          type: RacingActions.REMOVE_LOADER,
          payload: {
            error: null
          }
        })
      })
      .catch(error => {
        logger.error('[racing state]', 'unable to load event runners')
        dispatcher({
          type: RacingActions.REMOVE_LOADER,
          payload: {
            error
          }
        })
      })
  }

  const loadNextRaces = (payload) => {
    if (nextRacesLoadingRef.current) {
      // Skip if there's already pending nextRaces request
      return
    }

    nextRacesLoadingRef.current = true
    RacingManager.getNextRaces(AbortSignal.timeout(NEXT_RACES_LOADING_TIMEOUT_MS)).then(data => {
      dispatcher({
        type: RacingActions.RACING_NEXT_RACES,
        payload: {
          races: data?.races?.filter(r => raceHasAcitveMarkets(r)) || []
        }
      })
    }).catch(error => {
      logger.error('[racing state]', 'unable to load next races', error)
    }).finally(() => {
      nextRacesLoadingRef.current = false
    })
  }

  const loadSingleRaceHandler = (payload) => {
    dispatcher({
      type: RacingActions.ADD_LOADER
    })

    RacingManager.getRaceById(payload.raceId)
      .then((data) => {
        dispatcher({
          type: RacingActions.RACING_RACE_UPDATE,
          payload: data.race
        })
        dispatcher({
          type: RacingActions.REMOVE_LOADER,
          payload: {
            error: null
          }
        })
      })
      .catch((error) => {
        console.error(error)
        dispatcher({
          type: RacingActions.REMOVE_LOADER,
          payload: {
            error
          }
        })
      })
  }

  const raceStatusUpdateHandler = payload => {
    raceUpdateHandler(payload)
    const { status, startTime } = payload.data.race
    if (status === 'OPEN' && DateService.getTimeDifference(new Date(), startTime) < THIRTY_MINUTES) {
      // Reload next races after receiving raceStatusUpdate event
      // if race new status is open and time to go is less than 30 minutes
      loadNextRaces(payload)
    }
  }

  /*
  * Racing initialise
  */
  React.useEffect(() => {
    const ac = new AbortController()

    const init = async () => {
      dispatcher({
        type: RacingActions.ADD_LOADER
      })
      await RacingManager.getAvailableDates(ac.signal)
        .then(data => {
          if (data.dates == null || isArray(data.dates) === false) {
            throw new RacingError('No dates available', data)
          }
          return data
        })
        .then(data => {
          const marketsByType = (data.marketTypes || []).reduce((list, m) => {
            return {
              ...list,
              [m.type]: m
            }
          }, {})
          dispatcher({
            type: RacingActions.RACING_OPTIMISE_STATE,
            payload: {
              dates: data.dates,
              marketTypes: data.marketTypes,
              marketsByType: marketsByType,
              marketsByTypeConfig: marketsByType
              // raceDate: state.raceDate || data?.dates?.[0]?.date
            }
          })
          dispatcher({
            type: RacingActions.REMOVE_LOADER,
            payload: {
              error: null
            }
          })
        })
        .catch(error => {
          dispatcher({
            type: RacingActions.REMOVE_LOADER,
            payload: {
              error
            }
          })
        })

      dispatcher({
        type: RacingActions.ADD_LOADER
      })
      await RacingManager.getMeetsByDate(TODAY, ac.signal)
        .then(data => {
          dispatcher({
            type: RacingActions.RACING_MEETS_UPDATE,
            payload: data.meetings
          })
          dispatcher({
            type: RacingActions.REMOVE_LOADER,
            payload: {
              error: null
            }
          })

          loadNextRaces()
        })
        .catch(error => {
          dispatcher({
            type: RacingActions.REMOVE_LOADER,
            payload: {
              error
            }
          })
        })

      // Important that the FINAL action is to initialize (after all else)
      dispatcher({
        type: RacingActions.RACING_STATE_UPDATE,
        payload: {
          initialized: true
        }
      })
    }
    if (!state.initialized) {
      init()
    }

    Bus.emit(Bus.events.socket.subscribe, {
      subscriptionKeys: [{
        notificationTopic: RACING_ANY_RACE_STATUS_UPDATE,
        id: ''
      }]
    })

    Bus.on(Bus.events.racing.raceStatusUpdate, raceStatusUpdateHandler)
    Bus.on(Bus.events.racing.racingReInit, raceReInitHandler)
    Bus.on(Bus.events.racing.addBetslipRunners, addBetslipRunnersHandler)
    Bus.on(Bus.events.racing.clearBetslipRunners, clearBetslipRunnersHandler)
    Bus.on(Bus.events.racing.loadEventRunners, loadEventRunnersHandler)
    Bus.on(Bus.events.racing.loadNextRaces, loadNextRaces)
    Bus.on(Bus.events.racing.loadSingleRace, loadSingleRaceHandler)

    return () => {
      Bus.emit(Bus.events.socket.unsubscribe, {
        subscriptionKeys: [{
          notificationTopic: RACING_ANY_RACE_STATUS_UPDATE,
          id: ''
        }]
      })
      Bus.off(Bus.events.racing.raceStatusUpdate, raceStatusUpdateHandler)
      Bus.off(Bus.events.racing.racingReInit, raceReInitHandler)
      Bus.off(Bus.events.racing.addBetslipRunners, addBetslipRunnersHandler)
      Bus.off(Bus.events.racing.clearBetslipRunners, clearBetslipRunnersHandler)
      Bus.off(Bus.events.racing.loadEventRunners, loadEventRunnersHandler)
      Bus.off(Bus.events.racing.loadNextRaces, loadNextRaces)
      Bus.off(Bus.events.racing.loadSingleRace, loadSingleRaceHandler)

      ac.abort()
    }
  }, [connection.id, lang, state.initialized])

  /*
  * Retrieve Meets by date
  */
  React.useEffect(
    () => {
      if (isEmpty(state.raceDate) || isEmpty(state.dates)) {
        return
      }

      dispatcher({
        type: RacingActions.ADD_LOADER
      })

      const ac = new AbortController()
      const isToday = state.raceDate === state.dates[0].date
      RacingManager.getMeetsByDate(isToday ? TODAY : state.raceDate, ac.signal)
        .then(data => {
          dispatcher({
            type: RacingActions.RACING_MEETS_UPDATE,
            payload: data.meetings
          })

          dispatcher({
            type: RacingActions.REMOVE_LOADER,
            payload: {
              error: null
            }
          })
        })
        .catch(error => {
          dispatcher({
            type: RacingActions.REMOVE_LOADER,
            payload: {
              error
            }
          })
        })

      return () => {
        // Allow Meetings requests to complete
        // ac.abort()
      }
    },
    [connection.id, lang, state.raceDate, state.dates]
  )

  /*
  * Retrieve Active Race
  */
  React.useEffect(
    () => {
      if (state.activeRaceId == null) {
        return
      }

      dispatcher({
        type: RacingActions.ADD_LOADER
      })

      const ac = new AbortController()
      let acOddsHistories

      const raceRequest = RacingManager.getRaceById// RaceMarketsMultilegsTabs[state.activeMarketTypeId] ? RacingManager.getLegRaceById : RacingManager.getRaceById

      raceRequest(state.activeRaceId, ac.signal)
        .then(data => {
          if (data.race == null) {
            throw new RacingError('No race available', data)
          }
          return data
        })
        .then(data => {
          dispatcher({
            type: RacingActions.RACING_SET_ACTIVE_RACE,
            payload: data.race
          })
          dispatcher({
            type: RacingActions.REMOVE_LOADER,
            payload: {
              error: null
            }
          })

          acOddsHistories = loadOddsHistories(state.activeRaceId)
        })
        .catch(error => {
          dispatcher({
            type: RacingActions.REMOVE_LOADER,
            payload: {
              error
            }
          })
        })

      Bus.emit(Bus.events.socket.subscribe, {
        subscriptionKeys: [{
          notificationTopic: RacingActions.RACING_RACE_UPDATE,
          id: state.activeRaceId
        }]
      })
      Bus.on(Bus.events.racing.raceUpdate, raceUpdateHandler)

      return () => {
        Bus.emit(Bus.events.socket.unsubscribe, {
          subscriptionKeys: [{
            notificationTopic: RacingActions.RACING_RACE_UPDATE,
            id: state.activeRaceId
          }]
        })
        Bus.off(Bus.events.racing.raceUpdate, raceUpdateHandler)

        ac.abort()
        if (acOddsHistories) {
          acOddsHistories.abort()
        }
      }
    },
    [connection.id, lang, state.initialized, state.activeRaceId]
  )

  /*
  * Subscription for multiple race updates
  */
  React.useEffect(
    () => {
      if (state.subscribeRaceIds.length === 0 || !state.initialized) {
        return
      }

      state.subscribeRaceIds.forEach((raceId) => {
        Bus.emit(Bus.events.socket.subscribe, {
          subscriptionKeys: [{
            notificationTopic: RacingActions.RACING_RACE_UPDATE,
            id: raceId
          }]
        })
        Bus.on(Bus.events.racing.raceUpdate, raceUpdateHandler)
      })

      return () => {
        state.subscribeRaceIds.forEach((raceId) => {
          Bus.emit(Bus.events.socket.unsubscribe, {
            subscriptionKeys: [{
              notificationTopic: RacingActions.RACING_RACE_UPDATE,
              id: raceId
            }]
          })
          Bus.off(Bus.events.racing.raceUpdate, raceUpdateHandler)
        })

        dispatcher({
          type: RacingActions.ClEAR_ALL_SUBSCRIPTIONS,
          payload: {}
        })
      }
    },
    [state.initialized, state.subscribeRaceIds.length]
  )

  return [state, dispatcher]
}

const RacingContext = React.createContext()

export function RacingProvider (props) {
  const {
    value,
    children
  } = props

  const [state, dispatcher] = createRacingState(value)

  return (
    <RacingContext.Provider value={useMemo(
      () => [state, dispatcher],
      [state, dispatcher])}>
      {children}
    </RacingContext.Provider>
  )
}

export const useRacingState = () => {
  return React.useContext(RacingContext)
}

const selectMarketsByTypeConfig = () => {
  const [state] = useRacingState()
  return useMemo(() => state.marketsByTypeConfig, [state.marketsByTypeConfig])
}

const selectDates = () => {
  const [state] = useRacingState()
  return useMemo(() => state.dates, [state.dates])
}

const selectMarkets = () => {
  const [state] = useRacingState()
  return useMemo(() => state.markets, [state.markets])
}

const selectRaces = () => {
  const [state] = useRacingState()
  return useMemo(() => state.races, [state.races])
}

const selectRaceById = (raceId) => {
  const [state] = useRacingState()
  return useMemo(() => state.races.find((r) => r.raceId === raceId), [state.races, raceId])
}

const selectActiveRace = () => {
  const [state] = useRacingState()
  return useMemo(() => state.activeRace, [state.activeRace])
}

const selectActiveMeeting = () => {
  const [state] = useRacingState()
  return useMemo(() => state.activeMeet, [state.activeMeet])
}

const selectIsLoading = () => {
  const [state] = useRacingState()
  return !!state.loading
}

export const RacingSelector = {
  selectDates,
  selectMarkets,
  selectRaces,
  selectRaceById,
  selectActiveRace,
  selectActiveMeeting,
  selectIsLoading,
  selectMarketsByTypeConfig
}
